import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { CaseStatus, UserRole } from "../constants";
import { Calendar } from "primereact/calendar";
import { useState } from "react";
import { usePatchMedicalCase } from "../hooks/mutations";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Modal para informar a la clínica que debe agendar el caso
 * @param {object} props
 * @param {boolean} props.visible
 * @param {() => void} props.onHide
 * @param {string} props.patientName
 * @param {string} props.phoneNumber
 * @param {number} props.daysLeft
 * @param {number} props.caseId
 * @param {import("react").MutableRefObject} props.toastRef
 */
export default function HospitalPendingModal({
  visible,
  onHide,
  patientName,
  phoneNumber,
  daysLeft,
  caseId,
  toastRef,
  demoMode = false,
  onScheduleDemo = () => {},
}) {
  const role = localStorage.getItem("role");
  const [step, setStep] = useState(1);
  const [scheduleDate, setScheduleDate] = useState(null);

  const hideDialog = () => {
    setStep(1);
    setScheduleDate(null);
    onHide();
  };

  const queryClient = useQueryClient();
  const caseMutation = usePatchMedicalCase({
    onSuccess: ({ type, message }) => {
      if (type === "success") {
        toastRef.current.show({
          severity: "success",
          summary: "Éxito",
          detail: message,
        });
        queryClient.invalidateQueries({
          queryKey: ["medicalCase", Number(caseId)],
        });
        hideDialog();
      } else {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      }
    },
  });

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 35);

  if (role !== UserRole.MEDICAL_CENTER) {
    return <></>;
  }

  return (
    <Dialog
      id="hospital-pending-modal-demo"
      header="Caso Pendiente"
      visible={visible}
      onHide={hideDialog}
      draggable={false}
    >
      {step === 1 && (
        <div>
          <p>
            {demoMode ? (
              "Modo demostración: Simulando agendamiento de caso"
            ) : (
              `Este caso clínico está pendiente, tienes ${daysLeft} días para agendarlo`
            )}
          </p>
          <p>
            <b>Nombre del paciente:</b> {patientName}
          </p>
          <p>
            <b>Número de Teléfono:</b> {phoneNumber}
          </p>
          <div className="delete-confirm-buttons-container">
            <Button
              label="Cancelar"
              severity="secondary"
              onClick={hideDialog}
            />
            {/*<Button label="Agendar" onClick={() => setStep(2)} />*/}
            <Button 
              id="schedule-buttong"
              label={demoMode ? "Agendar (Demo)" : "Agendar"} 
              onClick={() => setStep(2)}
              severity={demoMode ? "help" : undefined}  // Cambia el color en demo
            />
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          {/*<p>Selecciona la fecha en la que el paciente será atendido</p>*/}
          <p>
            {demoMode
              ? "Simulación de selección de fecha (no se guardará)"
              : "Selecciona la fecha en la que el paciente será atendido"}
          </p>
          <div className="p-field p-fluid">
            <label htmlFor="scheduleDate">Fecha de atención</label>
            <Calendar
              id="scheduleDate"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.value)}
              showIcon
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              minDate={minDate}
              maxDate={maxDate}
              locale="es"
            />
          </div>
          <div className="delete-confirm-buttons-container">
            <Button
              label="Atrás"
              severity="secondary"
              onClick={() => setStep(1)}
              disabled={caseMutation.isPending}
            />
            <Button
              id="confirm-schedule-buttong"
              label={demoMode ? "Confirmar (Demo)" : "Agendar"}
              onClick={() => {
                if (demoMode) {
                  onScheduleDemo();  // Llama a la función demo
                  hideDialog();
                } else {
                  caseMutation.mutate({
                    caseId,
                    data: {
                      status: CaseStatus.RESCHEDULED,
                      schedule_date: scheduleDate,
                    },
                  });
                }
              }}
              disabled={!demoMode && (!scheduleDate || caseMutation.isPending)}
              loading={caseMutation.isPending && !demoMode}
              severity={demoMode ? "help" : undefined}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}
